import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Minus, Globe, Server, Zap, Building2 } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';

// ── Data ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'websites',   label: 'Websites',           icon: Globe },
  { id: 'servers',    label: 'Dedicated Servers',   icon: Server },
  { id: 'webapps',    label: 'Web Apps & PWAs',     icon: Zap },
  { id: 'enterprise', label: 'Enterprise Software',  icon: Building2 },
];

const PLANS = {
  websites: [
    {
      tier: '01', name: 'Starter', featured: false, sessions: '2 maintenance sessions per year',
      tagline: 'For small websites & simple online presence',
      yearly: '£400', oneTime: '£200', perUnit: 'per website', hours: '1-day session',
      sla: '5 business days response',
      featureGroups: [
        { label: "What's Included", items: ['CMS core & plugin updates','Security patches & vulnerability scan','Uptime monitoring review','Broken link & 404 audit','SSL certificate verification','Backup integrity check','Speed & performance review'] },
        { label: 'Session Report Includes', items: ['Plugin & theme patch changelog','Vulnerability scan results & closures','Lighthouse scores before & after fixes','Broken links repaired & 404s resolved','SSL expiry & backup verification log'] },
      ],
      cta: 'Get Started', ctaStyle: 'outline',
    },
    {
      tier: '02', name: 'Professional', featured: false, sessions: '3 maintenance sessions per year',
      tagline: 'For growing businesses with active web presence',
      yearly: '£900', oneTime: '£300', perUnit: 'per website', hours: '1-day session',
      sla: '3 business days response',
      featureGroups: [
        { label: 'Everything in Starter, plus', items: ['Advanced SEO health audit','Core Web Vitals optimisation','Database query optimisation','Redirect chain cleanup','Image compression & CDN review','Analytics & tracking validation','Cross-browser compatibility check'] },
        { label: 'Session Report Includes', items: ['SEO health scorecard with fix log','Core Web Vitals deltas & remediation steps','DB query optimisations applied','Redirect chains removed & canonicals fixed','Analytics tracking anomalies corrected'] },
      ],
      cta: 'Get Started', ctaStyle: 'outline',
    },
    {
      tier: '03', name: 'Business', featured: true, sessions: '4 maintenance sessions per year, 1 at each end of quarter',
      tagline: 'Full-service care for high-traffic, revenue-generating sites',
      yearlyFull: '£1,600', yearly: '£1,440', oneTime: '£400', yearlySaving: '£160', perUnit: 'per website', hours: '1-day session',
      sla: '24 hr response',
      featureGroups: [
        { label: 'Everything in Professional, plus', items: ['E-commerce flow & checkout testing','Third-party integrations audit','Staging environment testing','Full GDPR / cookie compliance review','Penetration test (surface-level)','Content & media library cleanup','Uptime SLA monitoring dashboard'] },
        { label: 'Session Report Includes', items: ['GDPR & cookie compliance remediation log','Surface-level pen test findings & patches','Checkout flow defects identified & fixed','Integration endpoints verified & documented','Executive health snapshot with risk ratings'] },
      ],
      cta: 'Get Started', ctaStyle: 'primary',
    },
    {
      tier: '04', name: 'Enterprise', featured: false, sessions: '4 maintenance sessions per year, 1 at each end of quarter',
      tagline: 'Comprehensive white-glove maintenance for enterprise web estates',
      yearlyFull: '£3,200', yearly: '£2,880', oneTime: '£800', yearlySaving: '£320', perUnit: 'per website', hours: '1-day session',
      sla: '4 hr priority response',
      featureGroups: [
        { label: 'Everything in Business, plus', items: ['Multi-domain / multi-site management','Custom SLA with escalation matrix','Dedicated account manager','Load testing & scalability benchmarking','Accessibility (WCAG 2.2) audit','Architecture review & roadmap'] },
        { label: 'Session Report Includes', items: ['Board-ready multi-site health summary','WCAG 2.2 violations found & resolved','Load test results with capacity thresholds','Architecture risks & remediation roadmap'] },
      ],
      cta: 'Contact Us', ctaStyle: 'outline',
    },
  ],
  servers: [
    {
      tier: '01', name: 'Starter', featured: false, sessions: '2 maintenance sessions per year',
      tagline: 'Single server, basic stack maintenance',
      yearly: '£800', oneTime: '£400', perUnit: 'per server', hours: '2–3 day session',
      sla: '5 business days response',
      featureGroups: [
        { label: "What's Included", items: ['OS-level updates & kernel patching','Web server config review (Nginx/Apache)','Firewall rules audit','Log file review & anomaly detection','Backup integrity verification','Disk usage & cleanup'] },
        { label: 'Session Report Includes', items: ['Kernel & package patch manifest with version diffs','Firewall rule changes applied & rationale','Log anomalies flagged & investigated','Disk reclaimed & cleanup actions taken','Backup restoration test outcome'] },
      ],
      cta: 'Get Started', ctaStyle: 'outline',
    },
    {
      tier: '02', name: 'Professional', featured: false, sessions: '3 maintenance sessions per year',
      tagline: 'Multi-service servers with DB and app stack',
      yearly: '£1,800', oneTime: '£600', perUnit: 'per server', hours: '2–3 day session',
      sla: '2 business days response',
      featureGroups: [
        { label: 'Everything in Starter, plus', items: ['Database optimisation & index tuning','Resource utilisation analysis','SSL/TLS configuration hardening','Container & service health check','Cron job audit','Fail2ban & intrusion detection review'] },
        { label: 'Session Report Includes', items: ['Slow query log with index optimisations applied','CPU/memory utilisation trends & thresholds','SSL/TLS cipher hardening actions taken','Container restart events investigated & resolved'] },
      ],
      cta: 'Get Started', ctaStyle: 'outline',
    },
    {
      tier: '03', name: 'Business', featured: true, sessions: '4 maintenance sessions per year, 1 at each end of quarter',
      tagline: 'High-availability infrastructure with redundancy',
      yearlyFull: '£3,200', yearly: '£2,880', oneTime: '£800', yearlySaving: '£320', perUnit: 'per server', hours: '2–3 day session',
      sla: '24 hr response',
      featureGroups: [
        { label: 'Everything in Professional, plus', items: ['Load balancer health & config review','Disaster recovery drill','Network performance benchmarking','Security penetration (infrastructure)','HA cluster failover test','Compliance posture check'] },
        { label: 'Session Report Includes', items: ['DR drill pass/fail outcomes & remediation steps','Network throughput benchmarks vs baseline','Infrastructure pen test findings & patches','Failover test results with RTO/RPO measurements','Compliance gap register with closure actions'] },
      ],
      cta: 'Get Started', ctaStyle: 'primary',
    },
    {
      tier: '04', name: 'Enterprise', featured: false, sessions: '4 maintenance sessions per year, 1 at each end of quarter',
      tagline: 'Mission-critical multi-region server estates',
      yearlyFull: '£7,200', yearly: '£6,480', oneTime: '£1,800', yearlySaving: '£720', perUnit: 'per server', hours: '2–3 day session',
      sla: '4 hr priority response',
      featureGroups: [
        { label: 'Everything in Business, plus', items: ['Multi-region / multi-DC coordination','Custom runbooks & documentation','Dedicated infrastructure engineer','SOC 2 / ISO 27001 prep support','24/7 escalation contact'] },
        { label: 'Session Report Includes', items: ['Multi-region uptime & latency health matrix','SOC 2 control gaps identified & addressed','Runbook changes & documentation updated','Executive infrastructure briefing with risk register'] },
      ],
      cta: 'Contact Us', ctaStyle: 'outline',
    },
  ],
  webapps: [
    {
      tier: '01', name: 'Starter', featured: false, sessions: '2 maintenance sessions per year',
      tagline: 'Small apps with minimal third-party dependencies',
      yearly: '£1,200', oneTime: '£600', perUnit: 'per web app', hours: 'Several days per session',
      sla: '5 business days response',
      featureGroups: [
        { label: "What's Included", items: ['Dependency & framework upgrades','API endpoint health checks','PWA service worker review','Security vulnerability scan','Browser compatibility testing','Basic Lighthouse audit'] },
        { label: 'Session Report Includes', items: ['Dependency upgrade log with breaking change notes','Lighthouse scores before & after patches','Security CVEs found & remediated','Browser compatibility defects fixed & verified'] },
      ],
      cta: 'Get Started', ctaStyle: 'outline',
    },
    {
      tier: '02', name: 'Professional', featured: false, sessions: '3 maintenance sessions per year',
      tagline: 'Production apps with automated test suites',
      yearly: '£2,700', oneTime: '£900', perUnit: 'per web app', hours: 'Several days per session',
      sla: '2 business days response',
      featureGroups: [
        { label: 'Everything in Starter, plus', items: ['Automated test suite run & triage','Performance profiling (Web Vitals)','Third-party integration validation','Caching strategy review','API rate limit & error monitoring'] },
        { label: 'Session Report Includes', items: ['Test suite pass/fail breakdown with flaky tests fixed','Web Vitals regressions diagnosed & resolved','Third-party integration failures investigated & closed','Cache invalidation issues identified & patched'] },
      ],
      cta: 'Get Started', ctaStyle: 'outline',
    },
    {
      tier: '03', name: 'Business', featured: true, sessions: '4 maintenance sessions per year, 1 at each end of quarter',
      tagline: 'Complex apps with CI/CD and multiple environments',
      yearlyFull: '£4,800', yearly: '£4,320', oneTime: '£1,200', yearlySaving: '£480', perUnit: 'per web app', hours: 'Several days per session',
      sla: '24 hr response',
      featureGroups: [
        { label: 'Everything in Professional, plus', items: ['CI/CD pipeline validation','Staging vs production diff review','GDPR & data privacy compliance check','UI regression testing','Auth & session security audit','Error tracking & alerting review'] },
        { label: 'Session Report Includes', items: ['CI/CD pipeline failures diagnosed & resolved','Staging vs production drift documented & reconciled','Auth & session vulnerabilities patched','GDPR data handling gaps closed','Error tracking alerts tuned & verified'] },
      ],
      cta: 'Get Started', ctaStyle: 'primary',
    },
    {
      tier: '04', name: 'Enterprise', featured: false, sessions: '4 maintenance sessions per year, 1 at each end of quarter',
      tagline: 'Large-scale apps with microservices architecture',
      yearlyFull: '£7,200', yearly: '£6,480', oneTime: '£1,800', yearlySaving: '£720', perUnit: 'per web app', hours: 'Several days per session',
      sla: '4 hr priority response',
      featureGroups: [
        { label: 'Everything in Business, plus', items: ['Microservices dependency mapping','API gateway & contract testing','Load testing & scalability report','Full architectural review','Dedicated engineering contact'] },
        { label: 'Session Report Includes', items: ['Microservices dependency map with failure points noted','API contract drift identified & versioning enforced','Load test thresholds & scaling bottlenecks documented','Architecture risk register with prioritised fixes'] },
      ],
      cta: 'Contact Us', ctaStyle: 'outline',
    },
  ],
  enterprise: [
    {
      tier: '01', name: 'Starter', featured: false, sessions: '2 maintenance sessions per year',
      tagline: 'Single internal tool or lightweight business system',
      yearly: '£1,600', oneTime: '£800', perUnit: 'per software', hours: 'Days to weeks per session',
      sla: '5 business days response',
      featureGroups: [
        { label: "What's Included", items: ['Codebase health review','Dependency & library updates','Bug triage & minor fixes','Database schema review','User access & permissions audit','Backup & data integrity check'] },
        { label: 'Session Report Includes', items: ['Codebase health metrics & smells logged','Library patch manifest with deprecation warnings','Bugs triaged, root-caused & resolved','DB schema anomalies found & corrected','Backup restoration test outcome'] },
      ],
      cta: 'Get Started', ctaStyle: 'outline',
    },
    {
      tier: '02', name: 'Professional', featured: false, sessions: '3 maintenance sessions per year',
      tagline: 'Multi-module business systems with integrations',
      yearly: '£3,600', oneTime: '£1,200', perUnit: 'per software', hours: 'Days to weeks per session',
      sla: '2 business days response',
      featureGroups: [
        { label: 'Everything in Starter, plus', items: ['Third-party integration health audit','API contract & versioning review','Performance profiling & tuning','Automated test suite review','Technical debt assessment','Documentation update'] },
        { label: 'Session Report Includes', items: ['Performance bottlenecks profiled & optimisations applied','Technical debt register with effort estimates','Integration contract failures investigated & resolved','Outdated documentation sections updated'] },
      ],
      cta: 'Get Started', ctaStyle: 'outline',
    },
    {
      tier: '03', name: 'Business', featured: true, sessions: '4 maintenance sessions per year, 1 at each end of quarter',
      tagline: 'Critical business systems with compliance requirements',
      yearlyFull: '£6,400', yearly: '£5,760', oneTime: '£1,600', yearlySaving: '£640', perUnit: 'per software', hours: 'Days to weeks per session',
      sla: '24 hr response',
      featureGroups: [
        { label: 'Everything in Professional, plus', items: ['Compliance audit (GDPR / ISO prep)','Security penetration testing','CI/CD pipeline review','Vendor & licence audit','Refactoring recommendations','Disaster recovery planning'] },
        { label: 'Session Report Includes', items: ['GDPR/ISO compliance gaps identified & closed','Pen test findings with severity ratings & patches applied','CI/CD failures diagnosed & pipeline hardened','Vendor licences audited & expiry risks flagged','DR plan tested with RTO/RPO outcomes recorded'] },
      ],
      cta: 'Get Started', ctaStyle: 'primary',
    },
    {
      tier: '04', name: 'Enterprise', featured: false, sessions: '4 maintenance sessions per year, 1 at each end of quarter',
      tagline: 'Mission-critical, multi-system enterprise platforms',
      yearlyFull: '£9,600', yearly: '£8,640', oneTime: '£2,400', yearlySaving: '£960', perUnit: 'per software', hours: 'Days to weeks per session',
      sla: '4 hr priority response',
      featureGroups: [
        { label: 'Everything in Business, plus', items: ['Full-platform architectural review','SOC 2 / ISO 27001 readiness','Dedicated senior engineer','Custom SLA & escalation matrix','Stakeholder reporting portal','Quarterly strategy session'] },
        { label: 'Session Report Includes', items: ['SOC 2 / ISO 27001 control gaps remediated & logged','Platform architectural risks ranked & actioned','Cross-system health map with failure dependency tree','Board-ready briefing with SLA adherence record'] },
      ],
      cta: 'Contact Us', ctaStyle: 'outline',
    },
  ],
};

const COMPARISON_ROWS = [
  { label: 'Sessions / Year',           vals: ['2', '3', '4', '4'],                    type: 'text' },
  { label: 'Detailed Session Report',   vals: [true, true, true, true],                 type: 'bool' },
  { label: 'Security Scanning',         vals: [true, true, true, true],                 type: 'bool' },
  { label: 'Performance Audit',         vals: [true, true, true, true],                 type: 'bool' },
  { label: 'Backup Verification',       vals: [true, true, true, true],                 type: 'bool' },
  { label: 'Database Optimisation',     vals: [false, true, true, true],                type: 'bool' },
  { label: 'Compliance Audit',          vals: [false, false, true, true],               type: 'bool' },
  { label: 'Penetration Testing',       vals: [false, false, true, true],               type: 'bool' },
  { label: 'Disaster Recovery Review',  vals: [false, false, true, true],               type: 'bool' },
  { label: 'Executive Summary Report',  vals: [false, false, 'partial', true],          type: 'bool' },
  { label: 'Custom SLA',                vals: [false, false, false, true],              type: 'bool' },
  { label: 'Dedicated Account Manager', vals: [false, false, false, true],              type: 'bool' },
  { label: 'SLA Response Time',         vals: ['5 days', '2–3 days', '24 hrs', '4 hrs'], type: 'text' },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const PlanCard = ({ plan, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className={`relative rounded-2xl border bg-white overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
        plan.featured
          ? 'border-[#003333] shadow-lg shadow-[#003333]/10'
          : 'border-slate-200'
      }`}
    >
      {/* Most Popular badge */}
      {plan.featured && (
        <div className="absolute top-0 right-0 bg-[#003333] text-white text-[10px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-bl-xl">
          Most Popular
        </div>
      )}

      {/* Header */}
      <div className="p-6 border-b border-slate-100">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#004D4D] mb-1">Tier {plan.tier}</p>
        <h3 className="font-bold text-[#003333] text-xl mb-1" style={{ fontFamily: "'Syne', sans-serif" }}>{plan.name}</h3>
        <p className="text-xs text-slate-500 leading-relaxed mb-4">{plan.tagline}</p>

        <div className="flex items-center gap-2 mb-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">Yearly Plan</p>
          {plan.yearlySaving && (
            <span className="inline-block bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
              Save 10%
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-1.5 mb-0.5">
          <span className="text-3xl font-bold text-[#003333]" style={{ fontFamily: "'Syne', sans-serif" }}>{plan.yearly}</span>
          <span className="text-xs text-slate-400">/ year</span>
          <span className="text-[10px] font-semibold text-slate-400 border border-slate-200 rounded-full px-2 py-0.5 leading-none">{plan.perUnit}</span>
        </div>
        {plan.yearlySaving && (
          <div className="flex items-center gap-1.5 mb-2">
            <span className="text-xs text-slate-400 line-through">{plan.yearlyFull}</span>
            <span className="text-xs font-semibold text-emerald-600">— save {plan.yearlySaving}</span>
          </div>
        )}
        <p className="text-xs text-slate-500 mb-2">
          One-time session: <span className="font-semibold text-[#003333]">{plan.oneTime}</span>
        </p>
        <span className="inline-flex items-center gap-1.5 bg-slate-100 rounded-full px-3 py-1 text-[11px] text-slate-500">
          <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          {plan.hours}
        </span>

        <div className="mt-3 inline-flex items-center gap-2 bg-[#F1F5F9] rounded-full px-3 py-1 text-[11px] text-[#004D4D] font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.2)]" />
          {plan.sla}
        </div>
      </div>

      {/* Body */}
      <div className="p-6 flex flex-col flex-1">
        <p className="text-xs font-semibold text-[#003333] mb-4 flex items-center gap-2">
          <span className="text-sm">↻</span> {plan.sessions}
        </p>

        <div className="flex-1 space-y-5">
          {plan.featureGroups.map((group) => (
            <div key={group.label}>
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 mb-2 pb-1.5 border-b border-slate-100">
                {group.label}
              </p>
              <ul className="space-y-1.5">
                {group.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[12.5px] text-slate-500 leading-snug">
                    <Check className="w-3 h-3 text-[#004D4D] flex-shrink-0 mt-0.5 font-bold" strokeWidth={3} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-6">
          <Link
            to="/consultation"
            className={`block w-full text-center py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
              plan.ctaStyle === 'primary'
                ? 'bg-[#003333] text-white hover:bg-[#004D4D]'
                : 'border border-[#003333] text-[#003333] hover:bg-[#003333]/5'
            }`}
          >
            {plan.cta}
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

const ComparisonCell = ({ val }) => {
  if (val === true)
    return <span className="text-green-500 text-base font-bold">✓</span>;
  if (val === false)
    return <span className="text-slate-300 text-base">—</span>;
  if (val === 'partial')
    return <span className="text-amber-400 text-xs font-semibold">Partial</span>;
  return <span className="text-white/70 text-sm">{val}</span>;
};

// ── Main Page ─────────────────────────────────────────────────────────────────

export const MaintenancePage = () => {
  const [activeTab, setActiveTab] = useState('websites');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const plans = PLANS[activeTab];

  return (
    <div className="min-h-screen bg-white" data-testid="maintenance-page">
      <Navigation />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-[#F8FAFC] relative overflow-hidden">
        {/* Subtle radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(0,51,51,0.05) 0%, transparent 70%)' }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#003333]/50 hover:text-[#003333] mb-8 transition-colors text-sm"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
              Management & Maintenance
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] text-[#003333] mb-6">
              Keep Your Systems<br />
              <span className="italic font-normal">Running Flawlessly.</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-500 max-w-2xl mb-8">
              Scheduled maintenance sessions per year with comprehensive reports —
              every update, fix, and improvement documented in detail.
            </p>

            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              {[
                { Icon: Globe, label: 'Websites' },
                { Icon: Server, label: 'Dedicated Servers' },
                { Icon: Zap, label: 'Web Apps & PWAs' },
                { Icon: Building2, label: 'Enterprise Software' },
              ].map(({ Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white text-xs font-medium text-slate-500"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#003333] block" />
                  {label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Plans ─────────────────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 bg-white" id="plans">
        <div className="max-w-7xl mx-auto px-6 md:px-12">

          {/* Section header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-12"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Choose Your Service</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#003333] mb-3">
              Maintenance Plans
            </h2>
            <p className="text-slate-500 max-w-lg leading-relaxed">
              Select a service category to view all four tiers — from lean starter packages to full enterprise-grade coverage.
            </p>
          </motion.div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-10" role="tablist">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-[#003333] border-[#003333] text-white shadow-sm'
                      : 'bg-white border-slate-200 text-slate-500 hover:border-[#003333] hover:text-[#003333]'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Cards grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 items-start"
            >
              {plans.map((plan, i) => (
                <PlanCard key={plan.name} plan={plan} index={i} />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── Comparison Table ──────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 bg-[#003333]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-12"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-3">At a Glance</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
              Plan Comparison
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="overflow-x-auto"
          >
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="text-left py-3 px-4 text-[11px] font-bold uppercase tracking-[0.12em] text-white/30 w-1/3">Feature</th>
                  {['Starter', 'Professional', 'Business', 'Enterprise'].map((name) => (
                    <th key={name} className="py-3 px-4 text-center text-white font-bold text-base border-b border-white/10">
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, i) => (
                  <tr key={row.label} className={`border-b border-white/[0.06] hover:bg-white/[0.03] transition-colors`}>
                    <td className="py-3.5 px-4 text-white/80 font-medium">{row.label}</td>
                    {row.vals.map((val, j) => (
                      <td key={j} className="py-3.5 px-4 text-center">
                        <ComparisonCell val={val} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* ── CTA Section ───────────────────────────────────────────────────── */}
      <section className="py-24 md:py-32 bg-[#F8FAFC] border-t border-slate-200 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">Not Sure?</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#003333] mb-4">
              Not Sure Which Plan<br />
              <span className="italic font-normal">Is Right for You?</span>
            </h2>
            <p className="text-slate-500 leading-relaxed mb-10 text-lg">
              Book a free 30-minute strategy call. We'll assess your systems and recommend
              the right maintenance tier — no commitment required.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/consultation"
                className="inline-flex items-center gap-2 bg-[#003333] text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-[#004D4D] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                Book a Free Consultation →
              </Link>
              <Link
                to="/instant-quote"
                className="inline-flex items-center gap-2 border border-[#003333] text-[#003333] px-8 py-4 rounded-full text-sm font-semibold hover:bg-[#003333]/5 transition-all duration-300"
              >
                Get Instant Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};